import React, { useEffect } from 'react';
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { useMediaQuery, useTheme } from '@mui/system';
import styles from "./lessonSurvey.module.css";
import { Box, Button, Radio, Stack, Typography } from '@mui/material';
import { Course } from '../../../interfaces/course';
import { pgGuidedJourneySurveyAnswered, pgGuidedJourneySurveyViewed } from '../../mixPanelEvents/mixPanelEvents';

const LessonSurvey = ({show, setShow, locationForMixpanel, options, setSelected, selected, onSubmit, title, labels}: {
    show: boolean,
    setShow: (value: boolean) => void,
    locationForMixpanel: string,
    options: Course[],
    setSelected: (value: number) => void,
    selected: number,
    onSubmit: () => void,
    title: string,
    labels: string[],
}) => {
    const [selectedOption, setSelectedOption] = React.useState("");
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const onlyMobile = useMediaQuery('(max-width: 767px)');
    const handleClose = () => {
        setShow(false);
    }
    const handleSubmit = () => {
        onSubmit();
        handleClose();
        pgGuidedJourneySurveyAnswered({
            Location: locationForMixpanel,
            programName: "Straight Shootin’ Golf 2.0",
            questionName: title,
            answer: selectedOption.toLowerCase().trim().replace(/\s+/g, '-')
        });
    }

    useEffect(() => {
        if(show) {
            setTimeout(() => {
                pgGuidedJourneySurveyViewed({ Location: locationForMixpanel, programName: "Straight Shootin’ Golf 2.0"});
            }, 1000);
        }
    }, [show]);

    return (
        <Dialog
            fullWidth
            fullScreen={isMobile}
            maxWidth={"md"}
            open={show}
            onClose={handleClose}
            sx={{padding: "13px", overflowY: "scroll !important", }}
            PaperProps={{
                sx: {
                    borderRadius: "8px",
                    maxWidth: "680px",
                    maxHeight: {
                        xs: "fit-content",
                        md: "100%",
                    }
                }
            }}
        >
            <DialogContent sx={{padding: "16px 40px"}} className={styles.dialogContent}>
                <Box 
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end", 
                        marginBottom: "24px",
                    }}>
                    <Box
                        onClick={handleClose}
                        sx={{
                            cursor: 'pointer',
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M5.29289 5.29289C5.68342 4.90237 6.31658 4.90237 6.70711 5.29289L12 10.5858L17.2929 5.29289C17.6834 4.90237 18.3166 4.90237 18.7071 5.29289C19.0976 5.68342 19.0976 6.31658 18.7071 6.70711L13.4142 12L18.7071 17.2929C19.0976 17.6834 19.0976 18.3166 18.7071 18.7071C18.3166 19.0976 17.6834 19.0976 17.2929 18.7071L12 13.4142L6.70711 18.7071C6.31658 19.0976 5.68342 19.0976 5.29289 18.7071C4.90237 18.3166 4.90237 17.6834 5.29289 17.2929L10.5858 12L5.29289 6.70711C4.90237 6.31658 4.90237 5.68342 5.29289 5.29289Z" fill="black"/>
                        </svg>
                    </Box>
                </Box>
                
                <Typography
                    variant="h5"
                    className={styles.modalTitle}
                >
                    {title}
                </Typography>

                <Typography
                    variant="h6"
                    className={styles.modalSubtitle}
                >
                    Please answer the question below and we’ll customize your training so you save time and get faster results.
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: "column",
                        marginBottom: "8px"
                    }}
                >
                    {labels?.length > 0 &&
                        labels.map((option, key) => {
                            const lessonId = key === 0
                            ? options[key]?.lessons[1]?.ID
                            : options[key]?.lessons[0]?.ID;
                            return (
                            <Stack
                                key={lessonId}
                                direction="column"
                                spacing={0.5}
                                onClick={() => {
                                    setSelectedOption(option);
                                    setSelected(lessonId);
                                }}
                            >
                                <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                className={styles.stack}
                                sx={{
                                    backgroundColor: selected === lessonId ? "#F4F2F0" : "",
                                    borderRadius: "12px",
                                    marginBottom: "16px !important",
                                    padding: "16px",
                                    boxShadow: "0 1px 2px -1px rgba(0, 0, 0, 0.10), 0 1px 3px 0 rgba(0, 0, 0, 0.10)"
                                }}
                                >
                                <Box sx={{ flex: "1" }}>
                                    <Typography
                                        className={styles.modalOption}
                                        sx={{ 
                                            userSelect: "none", 
                                        }}
                                    >
                                    {option}
                                    </Typography>
                                </Box>
                                <Radio
                                    className={styles.radio}
                                    sx={{
                                        '&.Mui-checked': { color: '#FD3300' }
                                    }}
                                    value={lessonId}
                                    checked={selected === lessonId}
                                    onChange={() => {
                                        setSelectedOption(option);
                                        setSelected(lessonId);
                                    }}
                                />
                                </Stack>
                            </Stack>
                            );
                        })
                    }
                </Box>
            </DialogContent>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    position: "sticky",
                    bottom: 0,
                    backgroundColor: "#fff",
                    paddingBottom: !onlyMobile ? "40px" : "24px",
                    margin: !onlyMobile ? "0 40px" : "0 24px"
                }}
            >
                <Button
                onClick={handleSubmit}
                className={styles.swingSurveyButton}
                fullWidth
                >
                <span>Submit</span>
                </Button>
            </Box>
        </Dialog>
    );
}

export default LessonSurvey;